import React, { useState, useEffect } from 'react';
import { Check, Crown, Zap, Award, Shield, TrendingUp, RefreshCw, XCircle, AlertCircle, CheckCircle } from 'lucide-react';
import { getUserSubscription, requestRefund, cancelSubscription, isInTrialPeriod } from './subscriptionUtils';
import { saveSubscription } from './services/firestoreService';
import { analytics } from './firebase';
import { logEvent } from 'firebase/analytics';

const Pricing = ({ currentUser, onNavigate }) => {
  const [userSubscription, setUserSubscription] = useState(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    if (currentUser) {
      getUserSubscription(currentUser.id).then(sub => setUserSubscription(sub));
    }
  }, [currentUser]);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 7000);
  };

  // Activate subscription in Firestore after successful Razorpay payment
  const activateSubscription = async (planName) => {
    try {
      const priceMap = { pro: 999, elite: 1499 };
      const startDate = new Date();
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + 1);

      const subscriptionData = {
        planId: planName,
        planName: planName.charAt(0).toUpperCase() + planName.slice(1),
        price: priceMap[planName] || 0,
        status: 'active',
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        createdAt: new Date().toISOString()
      };

      await saveSubscription(currentUser.id, subscriptionData);
      setUserSubscription(await getUserSubscription(currentUser.id));
      logEvent(analytics, 'plan_upgraded', { plan_name: planName });
      showNotification('success', `🎉 ${subscriptionData.planName} plan activated! Welcome aboard.`);
    } catch (err) {
      console.error('Activation error:', err);
      showNotification('error', 'Payment received but activation failed. Email support@tradefixai.in with your payment ID.');
    }
  };

  // Razorpay checkout — opens immediately on button click
  const handlePayment = (planName, amount) => {
    const options = {
      key: "rzp_live_SUzkcHqqqBjQ1o",
      amount: amount,
      currency: "INR",
      name: "TradeFix AI",
      description: planName + " Plan - Monthly",
      prefill: {
        name: currentUser?.displayName || '',
        email: currentUser?.email || ''
      },
      handler: function(response) {
        activateSubscription(planName.toLowerCase());
      },
      theme: { color: "#6366f1" }
    };
    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', function(response) {
      showNotification('error', 'Payment failed: ' + (response.error?.description || 'Please try again.'));
    });
    rzp.open();
  };

  // Handle refund request
  const handleRefund = async () => {
    if (!window.confirm('Are you sure you want to request a refund? This will cancel your subscription.')) return;
    const result = await requestRefund(currentUser.id);
    if (result.success) {
      setUserSubscription(await getUserSubscription(currentUser.id));
      showNotification('success', result.message);
    } else {
      showNotification('error', result.message);
    }
  };

  // Handle subscription cancellation
  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel? You will retain access until billing period ends.')) return;
    const result = await cancelSubscription(currentUser.id);
    if (result.success) {
      setUserSubscription(await getUserSubscription(currentUser.id));
      showNotification('success', result.message);
    } else {
      showNotification('error', result.message);
    }
  };

  const hasActivePlan = (planId) => {
    if (!userSubscription) return planId === 'basic';
    if (planId === 'basic' && userSubscription.status !== 'active') return true;
    if (userSubscription.status !== 'active') return false;
    if (new Date(userSubscription.endDate) < new Date()) return false;
    const activePlanId = userSubscription.planId === 'free' ? 'basic' : userSubscription.planId;
    return activePlanId === planId;
  };

  const currentPlanName = () => {
    if (!userSubscription || userSubscription.status !== 'active' || new Date(userSubscription.endDate) < new Date()) {
      return 'Basic (Free)';
    }
    return userSubscription.planName;
  };

  const isActivePaidSub = userSubscription &&
    userSubscription.status === 'active' &&
    new Date(userSubscription.endDate) > new Date();

  const isCancelled = userSubscription && userSubscription.status === 'cancelled';
  const isRefundRequested = userSubscription && userSubscription.status === 'refund_requested';
  const inTrial = isInTrialPeriod(currentUser);

  return (
    <div className="space-y-6">

      {/* Notification */}
      {notification && (
        <div className={`flex items-start space-x-3 p-4 rounded-lg border ${
          notification.type === 'success'
            ? 'bg-green-900/30 border-green-700 text-green-300'
            : 'bg-red-900/30 border-red-700 text-red-300'
        }`}>
          {notification.type === 'success'
            ? <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
            : <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
          }
          <p className="text-sm font-medium">{notification.message}</p>
        </div>
      )}

      {/* Header */}
      <div className="rounded-xl p-8 text-center" style={{ background: '#111827', border: '1px solid #1F2937' }}>
        <h2 className="text-3xl font-bold text-white mb-2">Choose Your Plan</h2>
        <p className="mb-4" style={{ color: '#9CA3AF' }}>Select the perfect plan for your trading journey</p>
        <div className="px-6 py-3 rounded-lg inline-block" style={{ background: '#0D1117', border: '1px solid #1F2937' }}>
          <p className="text-sm text-white">
            Current Plan: <span className="font-bold">{currentPlanName()}</span>
            {inTrial && !isActivePaidSub && (
              <span className="ml-2 bg-yellow-400 text-yellow-900 text-xs px-2 py-0.5 rounded-full font-bold">TRIAL</span>
            )}
          </p>
          {isActivePaidSub && (
            <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>
              Valid until: {new Date(userSubscription.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            </p>
          )}
          {isCancelled && (
            <p className="text-xs text-yellow-300 mt-1">
              Cancelled — access until {new Date(userSubscription.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            </p>
          )}
          {isRefundRequested && (
            <p className="text-xs text-yellow-300 mt-1">Refund request pending</p>
          )}
        </div>
      </div>

      {/* Subscription management */}
      {isActivePaidSub && (
        <div className="rounded-xl p-6" style={{ background: '#111827', border: '1px solid #1F2937' }}>
          <h3 className="text-lg font-bold text-white mb-4">Manage Subscription</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl p-4" style={{ background: '#0D1117', border: '1px solid #1F2937' }}>
              <p className="text-sm text-gray-400 mb-1">Plan</p>
              <p className="font-bold text-white">{userSubscription.planName}</p>
              <p className="text-sm text-gray-400">₹{userSubscription.price}/month</p>
            </div>
            <div className="rounded-xl p-4" style={{ background: '#0D1117', border: '1px solid #1F2937' }}>
              <p className="text-sm text-gray-400 mb-1">Next Billing</p>
              <p className="font-bold text-white">
                {new Date(userSubscription.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
              <p className="text-sm text-gray-400">
                Payment ID: {userSubscription.paymentId ? userSubscription.paymentId.slice(0, 16) + '...' : '—'}
              </p>
            </div>
          </div>
          <div className="flex space-x-3 mt-4">
            <button onClick={handleRefund} className="flex-1 flex items-center justify-center space-x-2 border border-orange-400 text-orange-400 py-2 px-4 rounded-lg font-medium hover:bg-orange-900/20 transition-colors">
              <RefreshCw size={16} /><span>Request Refund</span>
            </button>
            <button onClick={handleCancel} className="flex-1 flex items-center justify-center space-x-2 border border-red-400 text-red-400 py-2 px-4 rounded-lg font-medium hover:bg-red-900/20 transition-colors">
              <XCircle size={16} /><span>Cancel Subscription</span>
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-3 text-center">
            Refunds available within 14 days. Cancellation retains access until billing period ends.
          </p>
        </div>
      )}

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

        {/* Basic */}
        <div className="relative rounded-xl overflow-hidden" style={{ background: '#111827', border: '1px solid #1F2937' }}>
          <div className="p-6 text-center" style={{ borderBottom: '1px solid #1F2937' }}>
            <div className="inline-block p-3 rounded-full mb-3" style={{ background: '#0D1117' }}>
              <Zap size={32} className="text-gray-300" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-1">Basic</h3>
            <p className="text-sm text-gray-400 mb-3">Get started for free</p>
            <span className="text-4xl font-bold text-white">Free</span>
          </div>
          <div className="p-6">
            <ul className="space-y-3 mb-6">
              {['10 trades/month','Basic dashboard','Trade journal','Psychology Score','AI Assistant 3 chats/day','Mobile friendly'].map((f, i) => (
                <li key={i} className="flex items-start space-x-3">
                  <Check className="text-gray-300 flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-gray-300 text-sm">{f}</span>
                </li>
              ))}
            </ul>
            <button disabled className="w-full bg-gray-700 text-gray-400 py-3 rounded-lg font-medium cursor-not-allowed">
              {hasActivePlan('basic') ? '✓ Current Plan' : 'Default Plan'}
            </button>
          </div>
        </div>

        {/* Pro */}
        <div className="relative rounded-xl overflow-hidden transform scale-105" style={{ background: '#111827', border: '2px solid #3B82F6' }}>
          <div className="absolute top-0 right-0 text-white px-4 py-1 text-xs font-bold rounded-bl-lg" style={{ background: '#3B82F6' }}>RECOMMENDED</div>
          <div className="p-6 text-center" style={{ borderBottom: '1px solid #1F2937' }}>
            <div className="inline-block p-3 rounded-full mb-3" style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)' }}>
              <Crown size={32} className="text-purple-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-1">Pro</h3>
            <p className="text-sm text-gray-400 mb-3">Most Popular!</p>
            <div className="flex items-baseline justify-center">
              <span className="text-4xl font-bold text-white">₹999</span>
              <span className="text-gray-400 ml-1">/month</span>
            </div>
            <p className="text-xs text-green-400 font-medium mt-2">✓ 14-day money-back guarantee</p>
          </div>
          <div className="p-6">
            <ul className="space-y-3 mb-6">
              {['Everything in Basic','Unlimited trades','AI Coach insights','Psychology Tracker','Risk Manager','Live alerts','Top Traded Stocks','📊 Monthly Progress Report','Personal Trading Report','AI Insights','Community','AI Assistant Unlimited','Email support'].map((f, i) => (
                <li key={i} className="flex items-start space-x-3">
                  <Check className="text-purple-400 flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-gray-300 text-sm">{f}</span>
                </li>
              ))}
            </ul>
            {hasActivePlan('pro') ? (
              <button disabled className="w-full bg-green-500 text-white py-3 rounded-lg font-bold cursor-not-allowed opacity-80">✓ Current Plan</button>
            ) : (
              <>
                <button
                  onClick={() => handlePayment("Pro", 99900)}
                  className="w-full btn-primary py-3 rounded-lg font-bold transition-all"
                >
                  Get Pro — ₹999/mo
                </button>
                <p className="text-center text-xs text-gray-500 mt-2">🔒 Secured by Razorpay</p>
              </>
            )}
          </div>
        </div>

        {/* Elite */}
        <div className="relative rounded-xl overflow-hidden" style={{ background: '#111827', border: '1px solid rgba(234,179,8,0.4)' }}>
          <div className="absolute top-0 left-0 text-white px-3 py-1 text-xs font-bold rounded-br-lg" style={{ background: 'linear-gradient(135deg, #F59E0B, #EAB308)' }}>25% OFF — Limited Time</div>
          <div className="p-6 text-center pt-8" style={{ borderBottom: '1px solid #1F2937' }}>
            <div className="inline-block p-3 rounded-full mb-3" style={{ background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.2)' }}>
              <Award size={32} className="text-yellow-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-1">Elite</h3>
            <p className="text-sm text-gray-400 mb-3">For serious professionals</p>
            <div className="flex items-baseline justify-center space-x-2">
              <span className="text-lg text-gray-500 line-through">₹1,999</span>
              <span className="text-4xl font-bold text-white">₹1,499</span>
              <span className="text-gray-400">/month</span>
            </div>
            <p className="text-xs text-orange-400 font-medium mt-1">Early adopter price — full features coming soon!</p>
            <p className="text-xs text-green-400 font-medium mt-1">✓ 14-day money-back guarantee</p>
          </div>
          <div className="p-6">
            <ul className="space-y-3 mb-6">
              {['Everything in Pro','📅 Weekly Progress Report','📥 Import Trades (Coming Soon)','🔍 Hidden Charges Analysis (Coming Soon)','Export to Excel','1-on-1 onboarding','Dedicated WhatsApp support'].map((f, i) => (
                <li key={i} className="flex items-start space-x-3">
                  <Check className="text-yellow-400 flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-gray-300 text-sm">{f}</span>
                </li>
              ))}
            </ul>
            {hasActivePlan('elite') ? (
              <button disabled className="w-full bg-green-500 text-white py-3 rounded-lg font-bold cursor-not-allowed opacity-80">✓ Current Plan</button>
            ) : (
              <>
                <button
                  onClick={() => handlePayment("Elite", 149900)}
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white py-3 rounded-lg font-bold transition-all"
                >
                  Get Elite — ₹1,499/mo
                </button>
                <p className="text-center text-xs text-gray-500 mt-2">🔒 Secured by Razorpay</p>
              </>
            )}
          </div>
        </div>

      </div>

      {/* Feature comparison table */}
      <div className="rounded-xl p-6" style={{ background: '#111827', border: '1px solid #1F2937' }}>
        <h3 className="text-2xl font-bold mb-6 text-center text-white">Feature Comparison</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-700">
                <th className="text-left py-3 px-4 text-gray-300">Feature</th>
                <th className="text-center py-3 px-4 text-gray-300">Basic</th>
                <th className="text-center py-3 px-4 text-blue-400">Pro</th>
                <th className="text-center py-3 px-4 text-yellow-400">Elite</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: 'Trades', basic: '10/month', pro: 'Unlimited', elite: 'Unlimited' },
                { feature: 'Basic Dashboard', basic: '✓', pro: '✓', elite: '✓' },
                { feature: 'Trade Journal', basic: '✓', pro: '✓', elite: '✓' },
                { feature: 'Psychology Score', basic: '✓', pro: '✓', elite: '✓' },
                { feature: 'AI Assistant', basic: '3 chats/day', pro: 'Unlimited', elite: 'Unlimited' },
                { feature: 'Mobile Friendly', basic: '✓', pro: '✓', elite: '✓' },
                { feature: 'AI Coach Insights', basic: '✗', pro: '✓', elite: '✓' },
                { feature: 'Psychology Tracker', basic: '✗', pro: '✓', elite: '✓' },
                { feature: 'Risk Manager', basic: '✗', pro: '✓', elite: '✓' },
                { feature: 'Live Alerts', basic: '✗', pro: '✓', elite: '✓' },
                { feature: 'Monthly Progress Report', basic: '✗', pro: '✓', elite: '✓' },
                { feature: 'AI Insights', basic: '✗', pro: '✓', elite: '✓' },
                { feature: 'Email Support', basic: '✗', pro: '✓', elite: '✓' },
                { feature: 'Weekly Progress Report', basic: '✗', pro: '✗', elite: '✓' },
                { feature: 'Export to Excel', basic: '✗', pro: '✗', elite: '✓' },
                { feature: '1-on-1 Onboarding', basic: '✗', pro: '✗', elite: '✓' },
                { feature: 'WhatsApp Support', basic: '✗', pro: '✗', elite: '✓' },
                { feature: 'Money-back', basic: '—', pro: '14 days', elite: '14 days' },
              ].map((row, idx) => (
                <tr key={idx} className="border-b border-gray-800 hover:bg-gray-800/50">
                  <td className="py-3 px-4 text-gray-300 font-medium text-sm">{row.feature}</td>
                  <td className="py-3 px-4 text-center text-gray-500 text-sm">{row.basic}</td>
                  <td className="py-3 px-4 text-center text-blue-400 font-medium text-sm">{row.pro}</td>
                  <td className="py-3 px-4 text-center text-yellow-400 text-sm">{row.elite}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Money-back guarantee */}
      <div className="rounded-lg p-8 text-center" style={{ background: '#111827', border: '1px solid rgba(16,185,129,0.3)' }}>
        <Shield size={48} className="mx-auto mb-4 text-green-400" />
        <h3 className="text-2xl font-bold mb-2 text-white">14-Day Money-Back Guarantee</h3>
        <p className="mb-4" style={{ color: '#9CA3AF' }}>
          अगर आप पहले 14 दिनों में satisfied नहीं हैं, तो हम पूरा refund कर देंगे। No questions asked!
        </p>
        <div className="flex justify-center space-x-8 text-sm">
          <div><TrendingUp size={24} className="mx-auto mb-1" /><p>14-Day Trial</p></div>
          <div><Shield size={24} className="mx-auto mb-1" /><p>Secure Payment</p></div>
          <div><CheckCircle size={24} className="mx-auto mb-1" /><p>Cancel Anytime</p></div>
        </div>
      </div>

      {/* FAQs */}
      <div className="rounded-xl p-6" style={{ background: '#111827', border: '1px solid #1F2937' }}>
        <h3 className="text-2xl font-bold mb-6 text-white">Frequently Asked Questions</h3>
        <div className="space-y-5">
          {[
            { q: 'Basic plan में कितने trades हैं?', a: 'Basic plan में आप हर महीने 10 trades log कर सकते हैं। Unlimited trades के लिए Pro या Elite plan लें।' },
            { q: 'Payment कैसे होती है?', a: 'Razorpay के through secure payment होती है। UPI, Credit/Debit Card, Net Banking — सभी accept होते हैं। Payment instant process होती है।' },
            { q: 'क्या मैं plan बदल सकता हूं?', a: 'हां! आप कभी भी upgrade कर सकते हैं। नया plan तुरंत activate हो जाएगा।' },
            { q: 'Refund कैसे मिलेगा?', a: 'Purchase के 14 दिनों के अंदर Pricing page पर "Request Refund" button click करें। 5-7 business days में refund होगा।' },
            { q: 'Payment methods?', a: 'Credit Card, Debit Card, UPI (GPay, PhonePe, Paytm), Net Banking — सभी accept होते हैं via Razorpay।' },
          ].map((faq, idx) => (
            <div key={idx} className="border-b border-gray-700/50 pb-4 last:border-0 last:pb-0">
              <h4 className="font-bold text-white mb-1">{faq.q}</h4>
              <p className="text-gray-400 text-sm">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Support */}
      <div className="text-center py-6">
        <p className="text-gray-500 mb-2">Have questions? Need help choosing?</p>
        <a href="mailto:support@tradefixai.in" className="text-purple-400 font-medium hover:underline">Contact our support team</a>
      </div>

    </div>
  );
};

export default Pricing;
